// Central pricing display resolver.
// Kostaja moved to a value-based, offer-led model. Public numeric pricing is
// hidden site-wide. Every service is presented as an offer to accomplish a
// business objective; scope and pricing are confirmed after a short review.
// All price fields remain intact in services.js so per-service reactivation
// stays a one-line change: flip HIDE_ALL_PRICING to false.

const HIDE_ALL_PRICING = true;

// Services that were already positioned as proposal-led before the sitewide
// hide. Kept for the CTA/label override map below.
export const PROPOSAL_BASED_SLUGS = new Set([
  "brand-foundation-starter",
  "brand-foundation-core",
  "brand-foundation-complete",
  "website-foundation-starter",
  "website-growth-build",
  "website-system-complete",
  "brand-and-website-launch-foundation",
  "brand-and-website-growth-system",
  "complete-business-presence-system",
  "google-business-profile-growth-support",
  "google-business-profile-growth-support-plus",
  "local-visibility-starter-kit",
  "local-business-launch-kit",
  "marketing-support-retainer-starter",
  "marketing-support-growth",
  "digital-product-launch-support",
  "custom-project",
  "local-service-page-package",
]);

// Human-readable engagement type. Used on cards and detail pages.
// Falls back to a category-derived guess when not mapped.
const ENGAGEMENT_TYPE = {
  "strategy-call": "Strategy Call",
  "brand-audit": "Audit",
  "website-audit": "Audit",
  "brand-and-website-review": "Audit",
  "local-seo-snapshot": "Audit",
  "google-business-profile-audit": "Audit",
  "google-business-profile-setup": "Fixed-Scope Service",
  "google-business-profile-optimization": "Fixed-Scope Service",
  "google-business-profile-growth-support": "Ongoing Support",
  "google-business-profile-growth-support-plus": "Ongoing Support",
  "marketing-support-retainer-starter": "Ongoing Support",
  "marketing-support-growth": "Ongoing Support",
  "brand-foundation-starter": "Project",
  "brand-foundation-core": "Project",
  "brand-foundation-complete": "Project",
  "website-foundation-starter": "Project",
  "website-growth-build": "Project",
  "website-system-complete": "Project",
  "brand-and-website-launch-foundation": "Project",
  "brand-and-website-growth-system": "Project",
  "complete-business-presence-system": "Project",
  "local-visibility-starter-kit": "Project",
  "local-business-launch-kit": "Project",
  "digital-product-launch-support": "Project",
  "custom-project": "Project",
  "local-service-page-package": "Project",
};

// Display label used in place of the numeric price. When pricing is hidden
// site-wide, the label is derived from engagement type for a clean, offer-led
// presentation. Proposal-based services keep the sharper "Proposal-based" /
// "Scoped after consultation" labels.
const PROPOSAL_LABEL = {
  Project: "Proposal-based",
  "Ongoing Support": "Scoped after consultation",
};
const HIDDEN_LABEL_BY_TYPE = {
  Audit: "Available on request",
  "Strategy Call": "Available on request",
  "Fixed-Scope Service": "Available on request",
  "Ongoing Support": "Scoped after consultation",
  Project: "Proposal-based",
  Resource: "Sold through the shop",
};

// Category-derived scope factors used on the detail page when a service does
// not declare its own. Kept short and plain-English on purpose.
const SCOPE_FACTORS_BY_CATEGORY = {
  "brand-systems": [
    "Current brand condition and asset readiness",
    "Number of touchpoints the identity needs to cover",
    "Amount of copywriting and messaging work required",
    "Number of stakeholders and rounds of feedback",
    "Timeline and launch pressure",
  ],
  "website-systems": [
    "Page count and content readiness",
    "Number of service paths and industries covered",
    "Copywriting and portfolio content required",
    "Technical integrations (forms, analytics, booking, payments)",
    "Timeline and launch pressure",
  ],
  "brand-and-website-packages": [
    "Current brand condition and website state",
    "Page count, content, and portfolio readiness",
    "Copywriting, imagery, and asset needs",
    "Integrations and marketing systems in scope",
    "Timeline and stakeholder complexity",
  ],
  "local-visibility": [
    "Number of locations and service areas",
    "Current Google Business Profile condition",
    "Review and reputation baseline",
    "Ongoing support cadence",
    "Industry competitiveness in your region",
  ],
  "marketing-support": [
    "Cadence and channel mix",
    "Content asset readiness",
    "Approval flow and stakeholder count",
    "Reporting and measurement needs",
    "Length of the engagement",
  ],
  "custom-projects": [
    "Complexity of the outcome required",
    "Assets already in place",
    "Number of moving parts and integrations",
    "Timeline and stakeholder complexity",
    "Whether the work is one-time or ongoing",
  ],
};

const SCOPE_FACTORS_DEFAULT = [
  "Current condition of what exists",
  "Amount of new work required",
  "Timeline and launch pressure",
  "Number of stakeholders and approval steps",
  "Whether the work is one-time or ongoing",
];

// The single pricing helper the rest of the app calls.
export const resolvePricing = (service) => {
  const isProposal = PROPOSAL_BASED_SLUGS.has(service.slug);
  const engagementType =
    ENGAGEMENT_TYPE[service.slug] ||
    (isProposal ? "Project" : "Fixed-Scope Service");

  // Sitewide pricing hidden: every service uses a label, never a $ amount.
  const showPublicPrice = !HIDE_ALL_PRICING && !isProposal;

  let displayLabel;
  if (showPublicPrice) {
    displayLabel = service.priceLabel;
  } else if (isProposal) {
    displayLabel = PROPOSAL_LABEL[engagementType] || "Proposal-based";
  } else {
    displayLabel = HIDDEN_LABEL_BY_TYPE[engagementType] || "Available on request";
  }

  const scopeFactors =
    service.scopeFactors ||
    SCOPE_FACTORS_BY_CATEGORY[service.category] ||
    SCOPE_FACTORS_DEFAULT;

  // CTA overrides. Proposal-based services always route to /contact with a
  // "Request Scope" label. Preselection is passed via React Router link state
  // (no crawlable ?service=/intent= query string in the href) so Google is
  // never fed unnecessary parameter URL variants.
  let ctaLabelOverride = null;
  let ctaHrefOverride = null;
  let ctaStateOverride = null;

  if (isProposal) {
    ctaLabelOverride = "Request Scope";
    ctaHrefOverride = "/contact";
    ctaStateOverride = { serviceSlug: service.slug, intent: "quote" };
  } else if (HIDE_ALL_PRICING) {
    const kind = service.cta?.kind;
    // Keep native routing for book (Calendly) and shop (Shopify).
    // Rewrite stripe + quote + generic contact CTAs to a consistent phrase.
    if (kind === "stripe" || kind === "quote" || kind === "contact") {
      ctaLabelOverride = "Discuss This Service";
      ctaHrefOverride = "/contact";
      ctaStateOverride = { serviceSlug: service.slug, intent: null };
    }
  }

  return {
    showPublicPrice,
    displayLabel,
    engagementType,
    scopeFactors,
    ctaLabelOverride,
    ctaHrefOverride,
    ctaStateOverride,
  };
};
